/**
 * CART COMPONENT BASE UTILITIES
 *
 * Comprehensive base class for all cart-related Web Components, providing standardized patterns
 * for cart state management, event handling, error management, and component lifecycle.
 * Integrates seamlessly with CartStateManager for real-time cart operations.
 *
 * Used by: CartDrawer, CartItem, CartItems, CartGWPSelector, CartNote, CartDiscounts,
 *          CartAnnouncement, FreeShippingBanner, BuyMoreSaveMoreBanner
 *
 * Key Features:
 * - Unified cart state manager access via `this.cartStateManager` getter
 * - Standardized initialization sequence with async support
 * - Advanced event management with capture/replay for early events
 * - Comprehensive error handling with user feedback
 * - Loading state management with visual feedback
 * - Memory-efficient cleanup and resource management
 * - Debug logging support with component-specific namespacing
 * - DOM element caching for performance
 * - Timeout and AbortController tracking for proper cleanup
 *
 * Component Lifecycle:
 * 1. Constructor - Set up initial state and bind methods
 * 2. connectedCallback - Trigger initialization when added to DOM
 * 3. _initialize() - Execute standardized initialization sequence
 * 4. _cacheElements() - Cache frequently accessed DOM elements
 * 5. _initializeComponent() - Component-specific initialization (override)
 * 6. _setupEventListeners() - Set up event listeners (override)
 * 7. _markEventReady() - Enable event replay and mark component as ready
 * 8. disconnectedCallback - Clean up when removed from DOM
 *
 * Event System Integration:
 * - Automatic event capture for cart events before component is ready
 * - Event replay system ensures no events are missed during initialization
 * - Standardized event listener tracking for proper cleanup
 * - Support for both regular and replay-enabled event listeners
 *
 * Cart State Integration:
 * - Direct access to CartStateManager via `this.cartStateManager`
 * - Automatic waiting for cart state manager availability
 * - Integration with cart state for dynamic UI updates
 * - Real-time state synchronization across all components
 *
 * Error Handling:
 * - Centralized error handling with consistent logging
 * - User-friendly error messages with auto-hide functionality
 * - Error event dispatching for system-wide error management
 * - Recovery patterns for common failure scenarios
 *
 * @example
 * // Basic component extension
 * class MyCartComponent extends CartComponentBase {
 *   async _initializeComponent() {
 *     await this.waitForCartStateManager();
 *     const state = this.cartStateManager.getState();
 *     console.log('Current cart:', state);
 *   }
 *
 *   _setupEventListeners() {
 *     this._addEventListenerWithReplay(document, 'cart-state:changed', this.handleCartChange.bind(this));
 *   }
 * }
 *
 * @example
 * // Debug mode usage
 * <my-cart-component class="debug"></my-cart-component>
 *
 * @example
 * // Error handling
 * document.addEventListener('component-error', (event) => {
 *   console.error(`Error in ${event.detail.component}:`, event.detail.error);
 * });
 *
 * @namespace CartComponentBaseUtilities
 * @since 2024
 * @version 2.0.0
 */

import { getCartStateManager } from '@utilities/cart-state-manager';
import { parseConfig } from '@utilities/theme';

/**
 * Cart Component Base Class
 *
 * Provides standardized patterns for all cart-related components including:
 * - Debug logging
 * - Error handling
 * - Loading state management
 * - DOM element caching
 * - Event listener management
 * - Initialization sequence
 * - Memory cleanup
 * - Centralized cart state manager access via `this.cartStateManager`
 *
 * ## Usage
 * Subclasses should access the cart state manager using:
 * ```javascript
 * const stateManager = this.cartStateManager;
 * ```
 *
 * Do NOT create redundant `getCartStateManager()` methods in subclasses.
 *
 * @class CartComponentBase
 * @extends HTMLElement
 */
export class CartComponentBase extends HTMLElement {
  constructor() {
    super();

    /** @type {Object} Cached DOM elements */
    this.elements = {};

    /** @type {Object} Template elements */
    this.templates = {};

    /** @type {Object} Component configuration */
    this.config = null;

    /** @type {HTMLElement|null} Parent cart drawer */
    this.cartDrawer = null;

    /** @type {boolean} Whether component is currently loading */
    this.isLoading = false;

    /** @type {Array<number>} Active timeout IDs for cleanup */
    this.activeTimeouts = [];

    /** @type {Array<AbortController>} Active abort controllers for cleanup */
    this.activeControllers = [];

    /** @type {Map<string, Array>} Event listeners for cleanup */
    this.eventListeners = new Map();

    /** @type {Array<Object>} Events captured before listeners were ready */
    this.capturedEvents = [];

    /** @type {Set<string>} Event types that this component wants to capture */
    this.eventsToCapture = new Set();

    /** @type {boolean} Whether the component is ready to handle events */
    this.isEventReady = false;

    /** @type {Function|null} Global event capture handler */
    this.globalEventHandler = null;
  }

  /**
   * Checks if debug mode is enabled for this component
   * @returns {boolean} Whether debug logging is enabled
   */
  allowDebug() {
    return this.classList.contains("debug");
  }

  /**
   * Standard error handling with consistent logging and user feedback
   * @param {Error} error - The error that occurred
   * @param {string} context - Context where the error occurred
   * @param {boolean} showUser - Whether to show error to user
   * @protected
   */
  _handleError(error, context = '', showUser = false) {
    if (this.allowDebug()) {
      console.error(`${this.constructor.name}: ${context}`, error);
    }

    if (showUser) {
      this._showUserMessage(error.message || 'An error occurred', 'error');
    }

    // Dispatch standardized error event
    this.dispatchEvent(new CustomEvent('component-error', {
      bubbles: true,
      detail: { error, context, component: this.constructor.name }
    }));
  }

  /**
   * Set loading state with visual feedback
   * @param {boolean} isLoading - Whether component is loading
   * @param {string} context - Context for the loading state
   * @protected
   */
  _setLoadingState(isLoading, context = '') {
    this.isLoading = isLoading;
    this.classList.toggle(`${this.tagName.toLowerCase()}--loading`, isLoading);

    if (isLoading) {
      this._showSpinner();
    } else {
      this._hideSpinner();
    }

    if (this.allowDebug()) {
      console.log(`${this.constructor.name}: Loading state ${isLoading ? 'start' : 'end'} - ${context}`);
    }
  }

  /**
   * Show loading spinner
   * @protected
   */
  _showSpinner() {
    const spinner = this.elements.spinner || this.querySelector('.loading-overlay__spinner');
    if (spinner) {
      spinner.style.display = 'block';
    }
  }

  /**
   * Hide loading spinner
   * @protected
   */
  _hideSpinner() {
    const spinner = this.elements.spinner || this.querySelector('.loading-overlay__spinner');
    if (spinner) {
      spinner.style.display = 'none';
    }
  }

  /**
   * Get the cart state manager instance
   *
   * This is the standardized way for all CartComponentBase subclasses to access
   * the global cart state manager. Subclasses should use `this.cartStateManager`
   * rather than importing and calling `getCartStateManager()` directly.
   *
   * @returns {CartStateManager|null} The cart state manager instance
   * @protected
   */
  get cartStateManager() {
    return getCartStateManager();
  }

  /**
   * Get current cart state from the state manager
   * @returns {Object|null} Current cart state
   * @protected
   */
  getCartState() {
    return this.cartStateManager?.getState() || null;
  }

  /**
   * Wait for the cart state manager to be available and initialized
   *
   * Critical helper method that ensures components don't attempt to access cart state
   * before the CartStateManager is fully initialized. This prevents race conditions
   * during component startup and ensures reliable cart operations.
   *
   * The method uses multiple strategies:
   * - Immediate return if manager is already ready
   * - Event listeners for cart-system:ready and cart-state:initial-load
   * - Polling fallback every 100ms
   * - Configurable timeout with proper cleanup
   *
   * Use this in _initializeComponent() when you need immediate access to cart state
   * during component initialization, or before any cart operations that require
   * the state manager to be fully ready.
   *
   * @param {number} [timeout=10000] - Maximum wait time in milliseconds
   * @returns {Promise<CartStateManager>} Promise resolving with initialized cart state manager
   * @throws {Error} If cart state manager is not ready within timeout period
   * @protected
   *
   * @example
   * // In component initialization
   * async _initializeComponent() {
   *   const stateManager = await this.waitForCartStateManager();
   *   const currentState = stateManager.getState();
   *   this.updateUI(currentState);
   * }
   *
   * @example
   * // With custom timeout
   * const stateManager = await this.waitForCartStateManager(5000);
   */
  async waitForCartStateManager(timeout = 10000) {
    const startTime = Date.now();

    // If cart state manager is already available and initialized, return it immediately
    const existingManager = this.cartStateManager;
    if (existingManager && existingManager.initialStateLoaded) {
      if (this.allowDebug()) {
        console.log(`${this.constructor.name}: Cart state manager already ready`);
      }
      return existingManager;
    }

    if (this.allowDebug()) {
      console.log(`${this.constructor.name}: Waiting for cart state manager to be ready...`, {
        hasManager: !!existingManager,
        initialStateLoaded: existingManager?.initialStateLoaded
      });
    }

    return new Promise((resolve, reject) => {
      // Set up timeout
      const timeoutId = setTimeout(() => {
        if (this.allowDebug()) {
          console.warn(`${this.constructor.name}: Cart state manager wait timeout after ${timeout}ms`);
        }
        reject(new Error(`Cart state manager not ready within ${timeout}ms`));
      }, timeout);

      // Track timeout for cleanup
      this.activeTimeouts.push(timeoutId);

      // Function to check and resolve when ready
      const checkAndResolve = () => {
        const manager = this.cartStateManager;
        if (manager && manager.initialStateLoaded) {
          clearTimeout(timeoutId);
          // Remove from active timeouts since we cleared it manually
          const index = this.activeTimeouts.indexOf(timeoutId);
          if (index > -1) {
            this.activeTimeouts.splice(index, 1);
          }

          if (this.allowDebug()) {
            console.log(`${this.constructor.name}: Cart state manager ready after ${Date.now() - startTime}ms`);
          }
          resolve(manager);
          return true;
        }
        return false;
      };

      // Check immediately in case it became ready between our initial check and now
      if (checkAndResolve()) return;

      // Listen for cart state manager initialization events
      const handleCartSystemReady = () => {
        if (checkAndResolve()) {
          document.removeEventListener('cart-system:ready', handleCartSystemReady);
          document.removeEventListener('cart-state:initial-load', handleCartStateLoaded);
        }
      };

      const handleCartStateLoaded = () => {
        if (checkAndResolve()) {
          document.removeEventListener('cart-system:ready', handleCartSystemReady);
          document.removeEventListener('cart-state:initial-load', handleCartStateLoaded);
        }
      };

      // Listen for both events to catch different initialization scenarios
      document.addEventListener('cart-system:ready', handleCartSystemReady, { once: true });
      document.addEventListener('cart-state:initial-load', handleCartStateLoaded, { once: true });

      // Also poll every 100ms as a fallback
      const pollInterval = setInterval(() => {
        if (checkAndResolve()) {
          clearInterval(pollInterval);
          document.removeEventListener('cart-system:ready', handleCartSystemReady);
          document.removeEventListener('cart-state:initial-load', handleCartStateLoaded);
        }
      }, 100);

      // Clean up interval on timeout
      const originalTimeoutHandler = () => {
        clearInterval(pollInterval);
        document.removeEventListener('cart-system:ready', handleCartSystemReady);
        document.removeEventListener('cart-state:initial-load', handleCartStateLoaded);
      };

      // Replace timeout to include cleanup
      clearTimeout(timeoutId);
      const newTimeoutId = setTimeout(() => {
        originalTimeoutHandler();
        if (this.allowDebug()) {
          console.warn(`${this.constructor.name}: Cart state manager wait timeout after ${timeout}ms`);
        }
        reject(new Error(`Cart state manager not ready within ${timeout}ms`));
      }, timeout);

      // Update active timeouts array
      const timeoutIndex = this.activeTimeouts.indexOf(timeoutId);
      if (timeoutIndex > -1) {
        this.activeTimeouts[timeoutIndex] = newTimeoutId;
      } else {
        this.activeTimeouts.push(newTimeoutId);
      }
    });
  }

  /**
   * Cache frequently accessed DOM elements
   * Override in subclasses to define component-specific elements
   * @protected
   */
  _cacheElements() {
    this.elements = {
      spinner: this.querySelector('.loading-overlay__spinner'),
      errorContainer: this.querySelector('.error-container'),
      messageContainer: this.querySelector('.message-container')
    };

    this.templates = {};

    if (this.allowDebug()) {
      console.log(`${this.constructor.name}: Elements cached`, this.elements);
    }
  }

  /**
   * Standard initialization sequence
   * Override _initializeComponent() in subclasses for component-specific logic
   * @protected
   */
  async _initialize() {
    if (this.allowDebug()) {
      console.log(`${this.constructor.name}: Starting initialization`);
    }

    try {
      // 1. Cache DOM elements first
      this._cacheElements();

      // 2. Parse configuration using DOMUtils
      try {
        this.config = parseConfig(this, this.allowDebug());
      } catch (error) {
        console.error(`${this.constructor.name}: Error parsing config:`, error);
        this.config = {};
      }

      // 3. Dependencies are now imported via ES6 modules - no need to wait

      // 4. Component-specific initialization
      await this._initializeComponent();

      // 5. Set up event listeners
      this._setupEventListeners();

      // 6. Mark component as ready to handle events
      this._markEventReady();

      if (this.allowDebug()) {
        console.log(`${this.constructor.name}: Initialization complete`);
      }
    } catch (error) {
      this._handleError(error, 'initialization', true);
    }
  }

  /**
   * Component-specific initialization logic
   * Override in subclasses
   * @protected
   */
  async _initializeComponent() {
    // Override in subclasses
  }

  /**
   * Set up event listeners
   * Override in subclasses
   * @protected
   */
  _setupEventListeners() {
    // Override in subclasses
  }



  /**
   * Show user message with auto-hide
   * @param {string} message - Message to display
   * @param {string} type - Message type (info, error, success, warning)
   * @param {number} duration - Auto-hide duration in ms (0 = no auto-hide)
   * @protected
   */
  _showUserMessage(message, type = 'info', duration = 5000) {
    const container = this.elements.messageContainer;
    if (!container) return;

    container.textContent = message;
    container.className = `user-message user-message--${type}`;
    container.classList.remove('hidden');

    if (duration > 0) {
      const timeoutId = setTimeout(() => {
        this._hideUserMessage();
      }, duration);
      this.activeTimeouts.push(timeoutId);
    }
  }

  /**
   * Hide user message
   * @protected
   */
  _hideUserMessage() {
    const container = this.elements.messageContainer;
    if (container) {
      container.classList.add('hidden');
    }
  }

    /**
   * Add event listener with automatic cleanup tracking
   * @param {EventTarget} target - Event target
   * @param {string} type - Event type
   * @param {Function} listener - Event listener
   * @param {Object} options - Event listener options
   * @protected
   */
  _addEventListener(target, type, listener, options = {}) {
    target.addEventListener(type, listener, options);

    // Track for cleanup
    const key = `${target.constructor.name}-${type}`;
    if (!this.eventListeners.has(key)) {
      this.eventListeners.set(key, []);
    }
    this.eventListeners.get(key).push({ target, type, listener, options });

    if (this.allowDebug()) {
      console.log(`${this.constructor.name}: Added event listener for ${type} on ${target.constructor.name}`);
    }
  }

  /**
   * Remove a specific event listener
   * @param {EventTarget} target - Event target
   * @param {string} type - Event type
   * @param {Function} listener - Event listener to remove
   * @protected
   */
  _removeEventListener(target, type, listener) {
    target.removeEventListener(type, listener);

    // Remove from tracking
    const key = `${target.constructor.name}-${type}`;
    if (this.eventListeners.has(key)) {
      const listeners = this.eventListeners.get(key);
      const index = listeners.findIndex(l => l.listener === listener);
      if (index > -1) {
        listeners.splice(index, 1);
        if (listeners.length === 0) {
          this.eventListeners.delete(key);
        }
      }
    }

    if (this.allowDebug()) {
      console.log(`${this.constructor.name}: Removed event listener for ${type} on ${target.constructor.name}`);
    }
  }

  /**
   * Add event listener with advanced event replay capability for cart events
   *
   * Specialized event listener method that ensures cart components never miss critical
   * cart events, even if those events are dispatched before the component is fully
   * initialized. This prevents common race condition issues in cart systems.
   *
   * Features:
   * - Automatic event capture for cart-related events during initialization
   * - Event replay when component becomes ready
   * - Seamless integration with regular event listeners
   * - Proper cleanup and memory management
   *
   * Cart events that support replay:
   * - cart-state:changed, cart-state:loaded, cart-state:refreshed
   * - cart-state:item:added/removed/updated
   * - cart-system:ready
   *
   * @param {EventTarget} target - Event target (usually document for cart events)
   * @param {string} type - Event type (cart-* events get replay capability)
   * @param {Function} listener - Event listener function
   * @param {Object} [options={}] - Event listener options
   * @protected
   *
   * @example
   * // Setup cart state change listener with replay
   * this._addEventListenerWithReplay(document, 'cart-state:changed', (event) => {
   *   this.handleCartStateChange(event.detail.state);
   * });
   *
   * @example
   * // Regular event (no replay needed)
   * this._addEventListenerWithReplay(this, 'click', this.handleClick);
   */
  _addEventListenerWithReplay(target, type, listener, options = {}) {
    // Add to capture list if it's a cart-related event
    if (type.startsWith('cart-')) {
      this.eventsToCapture.add(type);

      // Start capturing if not already started
      if (!this.globalEventHandler) {
        this._startEventCapture();
      }
    }

    // Add the regular event listener
    this._addEventListener(target, type, listener, options);

    // If component is ready and we have captured events of this type, replay them
    if (this.isEventReady) {
      this._replayEventsOfType(type, listener);
    }
  }

  /**
   * Start capturing global cart events before listeners are ready
   * @private
   */
  _startEventCapture() {
    if (this.globalEventHandler) return;

    this.globalEventHandler = (event) => {
      // Only capture events we're interested in
      if (this.eventsToCapture.has(event.type) && !this.isEventReady) {
        if (this.allowDebug()) {
          console.log(`${this.constructor.name}: Capturing event before ready:`, {
            type: event.type,
            timestamp: Date.now(),
            hasDetail: !!event.detail
          });
        }

        // Store a copy of the event data
        this.capturedEvents.push({
          type: event.type,
          detail: event.detail ? JSON.parse(JSON.stringify(event.detail)) : null,
          timestamp: Date.now(),
          target: event.target
        });

        // Limit captured events to prevent memory issues
        if (this.capturedEvents.length > 50) {
          this.capturedEvents.shift();
        }
      }
    };

    // Use capture phase to ensure we get events early
    document.addEventListener('cart-state:changed', this.globalEventHandler, { capture: true });
    document.addEventListener('cart-state:loaded', this.globalEventHandler, { capture: true });
    document.addEventListener('cart-state:refreshed', this.globalEventHandler, { capture: true });
    document.addEventListener('cart-state:item:added', this.globalEventHandler, { capture: true });
    document.addEventListener('cart-state:item:removed', this.globalEventHandler, { capture: true });
    document.addEventListener('cart-state:item:updated', this.globalEventHandler, { capture: true });
    document.addEventListener('cart-system:ready', this.globalEventHandler, { capture: true });
  }

  /**
   * Replay captured events of a specific type to a listener
   * @param {string} eventType - Event type to replay
   * @param {Function} listener - Listener function to call
   * @private
   */
  _replayEventsOfType(eventType, listener) {
    const eventsToReplay = this.capturedEvents.filter(event => event.type === eventType);

    if (eventsToReplay.length === 0) return;

    if (this.allowDebug()) {
      console.log(`${this.constructor.name}: Replaying ${eventsToReplay.length} captured events of type ${eventType}`);
    }

    eventsToReplay.forEach((eventData, index) => {
      try {
        // Create a synthetic event with the captured data
        const syntheticEvent = new CustomEvent(eventData.type, {
          detail: eventData.detail
        });

        // Add metadata to indicate this is a replayed event
        syntheticEvent.detail = {
          ...eventData.detail,
          _isReplayed: true,
          _originalTimestamp: eventData.timestamp,
          _replayTimestamp: Date.now()
        };

        if (this.allowDebug()) {
          console.log(`${this.constructor.name}: Replaying event ${index + 1}/${eventsToReplay.length}:`, {
            type: eventData.type,
            originalTimestamp: eventData.timestamp,
            replayDelay: Date.now() - eventData.timestamp
          });
        }

        listener.call(this, syntheticEvent);
      } catch (error) {
        console.error(`${this.constructor.name}: Error replaying event:`, error, eventData);
      }
    });
  }

  /**
   * Mark the component as ready to handle events and replay any captured events
   * @protected
   */
  _markEventReady() {
    if (this.isEventReady) return;

    this.isEventReady = true;

    if (this.allowDebug()) {
      console.log(`${this.constructor.name}: Component marked as event ready, captured ${this.capturedEvents.length} events`);
    }

    // Stop capturing new events
    this._stopEventCapture();

    // Clear captured events after a delay to allow for any final replays
    this._setTimeout(() => {
      if (this.allowDebug() && this.capturedEvents.length > 0) {
        console.log(`${this.constructor.name}: Clearing ${this.capturedEvents.length} captured events`);
      }
      this.capturedEvents = [];
    }, 1000);
  }

  /**
   * Stop capturing global cart events
   * @private
   */
  _stopEventCapture() {
    if (!this.globalEventHandler) return;

    document.removeEventListener('cart-state:changed', this.globalEventHandler, { capture: true });
    document.removeEventListener('cart-state:loaded', this.globalEventHandler, { capture: true });
    document.removeEventListener('cart-state:refreshed', this.globalEventHandler, { capture: true });
    document.removeEventListener('cart-state:item:added', this.globalEventHandler, { capture: true });
    document.removeEventListener('cart-state:item:removed', this.globalEventHandler, { capture: true });
    document.removeEventListener('cart-state:item:updated', this.globalEventHandler, { capture: true });
    document.removeEventListener('cart-system:ready', this.globalEventHandler, { capture: true });

    this.globalEventHandler = null;
  }

  /**
   * Create a timeout with automatic cleanup tracking
   * @param {Function} callback - Callback function
   * @param {number} delay - Delay in milliseconds
   * @returns {number} Timeout ID
   * @protected
   */
  _setTimeout(callback, delay) {
    const timeoutId = setTimeout(callback, delay);
    this.activeTimeouts.push(timeoutId);
    return timeoutId;
  }

  /**
   * Create an AbortController with automatic cleanup tracking
   * @returns {AbortController} Abort controller
   * @protected
   */
  _createAbortController() {
    const controller = new AbortController();
    this.activeControllers.push(controller);
    return controller;
  }

  /**
   * Clear all timeouts
   * @protected
   */
  _clearTimeouts() {
    this.activeTimeouts.forEach(timeoutId => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    });
    this.activeTimeouts = [];
  }

  /**
   * Remove all tracked event listeners
   * @protected
   */
  _removeEventListeners() {
    this.eventListeners.forEach((listeners) => {
      listeners.forEach(({ target, type, listener, options }) => {
        target.removeEventListener(type, listener, options);
      });
    });
    this.eventListeners.clear();
  }

  /**
   * Abort all tracked controllers
   * @protected
   */
  _abortControllers() {
    this.activeControllers.forEach(controller => {
      if (!controller.signal.aborted) {
        controller.abort();
      }
    });
    this.activeControllers = [];
  }

  /**
   * Clear object references
   * @protected
   */
  _clearReferences() {

    this.cartDrawer = null;
    this.elements = {};
    this.templates = {};
    this.config = null;
    this.capturedEvents = [];
    this.eventsToCapture.clear();
    this.isEventReady = false;
  }

  /**
   * Standard cleanup sequence
   * @protected
   */
  _cleanup() {
    if (this.allowDebug()) {
      console.log(`${this.constructor.name}: Cleaning up component`);
    }

    // Stop event capture
    this._stopEventCapture();

    // Clear all timeouts/intervals
    this._clearTimeouts();

    // Remove all event listeners
    this._removeEventListeners();

    // Abort all controllers
    this._abortControllers();

    // Clear references
    this._clearReferences();
  }

  /**
   * Web Components lifecycle - called when connected to DOM
   */
  async connectedCallback() {
    await this._initialize();
  }

  /**
   * Web Components lifecycle - called when disconnected from DOM
   */
  disconnectedCallback() {
    this._cleanup();
  }
}

export default CartComponentBase;
